import React, { useCallback, useContext, useMemo, useState } from 'react'

import { Dropdown, Menu } from 'antd'
import { faClockThreeThirty, faDownload, faFileExport, faList } from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'

import { isCoordinator, isEncoder } from '../../../../utils/roles'
import { SchoolContext } from '../../../../Providers/SchoolProvider'
import { selectUser } from '../../../../reducers/UserReducer'
import { deleteStudent, getAllStudents, SerializationContexts as _SC, exportStudents } from '../../../../utils/api/student'
import useRequest from '../../../../hooks/useRequest'
import { TableParameters } from '../../../../utils/entities/tableParameters'
import StudentsTable from './StudentsTable'
import LinkStudentModal from './LinkStudentModal'
import { downloadFile, getIdOrNull, isObject } from '../../../../utils'
import { OrderBy, OrderDirections } from '../../../../utils/constants'
import StudentFormModal from '../../../../Providers/Forms/StudentForm/StudentFormModal'
import { Student } from '../../../../utils/entities/student'
import TooltipIconButton from '../../../../Components/antd/Buttons/TooltipIconButton'
import IconLabel from '../../../../Components/icons/IconLabel'
import { onError, onInfo, onSuccess } from '../../../../utils/apiHelper'
import StudentsImportModal from '../../../../Components/school/StudentsImportModal'
import ModalHours from '../../../../Components/student/ModalHours'
import { selectCurrentLanguage, selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { getDocument } from '../../../../utils/api/export'

import '../../../../assets/school-students-view.scss'

const StudentsListView = ({ school }) => {
  const { useHoursExport } = useContext(SchoolContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)
  const currentLanguage = useSelector(selectCurrentLanguage)

  const [tableParameters, setTableParameters] = useState(new TableParameters({ order: { by: OrderBy.SECTIONS, direction: OrderDirections.ASC }, pageSize: 50 }))
  const [studentToLink, setStudentToLink] = useState(null)
  const [displayHoursModal, setDisplayHoursModal] = useState(false)
  const [displayUploadModal, setDisplayUploadModal] = useState(false)
  const [displayedColumns, setDisplayedColumns] = useState([])

  const permissions = useMemo(() => ({
    canEditStudent: isEncoder(user) || isCoordinator(user),
    canResetPassword: isEncoder(user),
    canSeeAllInformations: isEncoder(user) || isCoordinator(user)
  }), [user])

  const students = useRequest({
    request: getAllStudents,
    parameters: useMemo(() => tableParameters.toQueryParameters({
      school: isObject(school) ? getIdOrNull(school) : undefined,
      contexts: [_SC.NISS, _SC.STATS]
    }), [tableParameters, school]),
    defaultMetadata: tableParameters.metaData,
    select: useCallback(data => data.map(s => new Student(s)), [])
  })

  const handleDelete = student => {
    deleteStudent(user, student).then(() => {
      students.reload()
    })
  }

  const handleChange = () => {
    students.reload()
  }

  const downloadTemplate = () => {
    onInfo(t('students_view.export.downloading_template.loading'))
    downloadFile('/students/download', 'students_template.xlsx', user).then(() => {
      onSuccess(t('students_view.export.downloading_template.success'))
    }).catch(() => {
      onError(t('students_view.export.downloading_template.error'))
    })
  }

  const handleStudentsExport = () => {
    onInfo(t('common.start_export'))
    exportStudents(user, {
      order: tableParameters.order,
      language: currentLanguage,
      filters: { ...tableParameters.filters, columns: ['id', ...displayedColumns] }
    }).then(({ job }) => getDocument(
      job,
      user,
      () => onError(t('spreadsheet_download.error'))
    )).catch(error => {
      console.error(error.message)
      onError(t('internships_export.error'))
    })
  }

  const exporter = () => (
    <Dropdown
      trigger={['click']}
      overlay={
        <Menu>
          <Menu.Item onClick={handleStudentsExport}>
            <IconLabel icon={faList} text={t('students_view.export.list')} />
          </Menu.Item>
          {useHoursExport && (
            <Menu.Item onClick={() => setDisplayHoursModal(true)}>
              <IconLabel icon={faClockThreeThirty} text={t('students_view.export.hours')} />
            </Menu.Item>
          )}
          <Menu.Item onClick={downloadTemplate}>
            <IconLabel icon={faDownload} text={t('students_view.export.download_template')} />
          </Menu.Item>
          <Menu.Item onClick={() => setDisplayUploadModal(true)}>
            <IconLabel icon={faFileExport} text={t('students_view.import.students')} />
          </Menu.Item>
        </Menu>
      }
    >
      <TooltipIconButton icon={faFileExport} title={t('Export')} />
    </Dropdown>
  )

  return (
    <div>
      <StudentFormModal>
        <StudentsTable
          canResetPassword={permissions.canResetPassword}
          canSeeAllInformations={permissions.canSeeAllInformations}
          canViewActivity={useHoursExport}
          data={students.data}
          exporter={exporter}
          loading={students.loading}
          parameters={{ ...tableParameters, metadata: students.metadata }}
          onChange={handleChange}
          onDelete={handleDelete}
          onKeysChanged={setDisplayedColumns}
          onParametersChange={setTableParameters}
          reload={students.reload}
        />
      </StudentFormModal>
      <LinkStudentModal student={studentToLink} onClose={() => setStudentToLink(null)} />
      <StudentsImportModal
        displayed={displayUploadModal}
        onClose={() => setDisplayUploadModal(false)}
        reloadStudentsAfterImport={students.reload}
      />
      <ModalHours visible={displayHoursModal} onClose={() => setDisplayHoursModal(false)} />

    </div>
  )
}

export default StudentsListView
